import { useEffect, useState } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { deleteLayerByCustomId } from '@utils/map/helpers';

/**
 * Creates and registers a plain OL VectorLayer on the map for the lifetime of
 * the calling component, then removes it on unmount. Plain-VectorLayer sibling
 * of useWebGLVectorLayer — use this instead when the feature count is small
 * enough that WebGL's shader-based rendering isn't needed and a per-feature
 * Style function (rather than a WebGL flat-style expression) is preferred.
 *
 * @param {Object} mapObject - OpenLayers map instance
 * @param {string} layerId - Unique customLayerId for the layer
 * @param {Object} [options] - Layer configuration
 * @param {string} [options.name='Layer'] - Display name for the layer
 * @param {number} [options.zIndex=10] - Stacking order
 * @param {boolean} [options.visible=false] - Initial visibility
 * @returns {import('ol/layer/Vector').default|null} The layer instance (null until created)
 */
export const useVectorLayer = (
	mapObject,
	layerId,
	{ name = 'Layer', zIndex = 10, visible = false } = {}
) => {
	const [layer, setLayer] = useState(null);

	useEffect(() => {
		if (!mapObject) return;

		const layerInstance = new VectorLayer({
			name,
			zIndex,
			visible,
			source: new VectorSource({}),
			properties: { customLayerId: layerId },
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);

		return () => {
			deleteLayerByCustomId(mapObject, layerId);
			setLayer(null);
		};
	}, [mapObject, layerId]);

	// Keep the layer's stacking order in sync when zIndex changes after creation
	useEffect(() => {
		layer?.setZIndex(zIndex);
	}, [layer, zIndex]);

	return layer;
};
